<template>
  <div class="tool-bar">
    <div class="tagline">{{ $t('common.pageTagline') }}</div>
    <header class="landing-header">
      <div class="header-inner">
        <div class="header-brand">
          <img :src="require('@/assets/image/common/tool-bar-logo.png')" alt="" class="header-logo-icon">
          <span class="header-logo-text"><span class="header-logo-bold">MYTutor Math</span><span class="header-logo-light"></span></span>
        </div>
      </div>
    </header>
    <div class="user-info">
      <button
        v-if="isVerifyPage"
        type="button"
        class="toolbar-home-btn"
        @click="goHome"
      >
        <i class="el-icon-house" aria-hidden="true" />
        <span>Home</span>
      </button>
      <template v-else>
        <span
          v-if="isStudent && licenseExpireLabel"
          :class="['license-expire-badge', { 'license-expire-badge--expired': isLicenseExpired }]"
        >
          <i class="el-icon-time" aria-hidden="true" />
          <span>{{ licenseExpireLabel }}</span>
        </span>
        <el-dropdown
          trigger="click"
          @command="handleCommand"
        >
        <span class="user-dropdown-trigger el-dropdown-link" role="button" tabindex="0">
          <i class="el-icon-user-solid user-trigger-icon" aria-hidden="true" />
          <span class="user-trigger-name">{{ userInfo.userAccount }}</span>
          <i class="el-icon-arrow-down user-trigger-caret" aria-hidden="true" />
        </span>
        <!-- Element UI 2 不支持在 el-dropdown 上使用 popper-class；class 需写在 el-dropdown-menu 上才会挂到 ul.popper -->
        <el-dropdown-menu class="toolbar-user-dropdown">
          <el-dropdown-item
            v-if="userInfo.type === 9"
            command="teacherManagement"
            class="toolbar-dd-item toolbar-dd-item--teacher"
          >
            <span class="toolbar-dd-inner">
              <i class="el-icon-s-management" aria-hidden="true" />
              <span class="toolbar-dd-text">{{ $t('common.teacherManagement') }}</span>
            </span>
          </el-dropdown-item>
          <el-dropdown-item command="profile" class="toolbar-dd-item toolbar-dd-item--profile">
            <span class="toolbar-dd-inner">
              <i class="el-icon-user-solid" aria-hidden="true" />
              <span class="toolbar-dd-text">{{ $t('common.myProfile') }}</span>
            </span>
          </el-dropdown-item>
          <el-dropdown-item command="changeCredentials" class="toolbar-dd-item toolbar-dd-item--profile">
            <span class="toolbar-dd-inner">
              <i class="el-icon-lock" aria-hidden="true" />
              <span class="toolbar-dd-text">{{ $t('common.changeAccountPassword') }}</span>
            </span>
          </el-dropdown-item>
          <el-dropdown-item command="billing" class="toolbar-dd-item toolbar-dd-item--profile">
            <span class="toolbar-dd-inner">
              <i class="el-icon-bank-card" aria-hidden="true" />
              <span class="toolbar-dd-text">{{ $t('common.billing') }}</span>
            </span>
          </el-dropdown-item>
          <el-dropdown-item command="buy" class="toolbar-dd-item toolbar-dd-item--profile">
            <span class="toolbar-dd-inner">
              <i class="el-icon-bank-card" aria-hidden="true" />
              <span class="toolbar-dd-text">{{ $t('common.buy') }}</span>
            </span>
          </el-dropdown-item>
          <el-dropdown-item command="logout" class="toolbar-dd-item toolbar-dd-item--logout">
            <span class="toolbar-dd-inner">
              <i class="el-icon-switch-button" aria-hidden="true" />
              <span class="toolbar-dd-text">{{ $t('common.Logout') }}</span>
            </span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      </template>
    </div>
    <el-dialog
      :visible.sync="profileVisible"
      width="520px"
      custom-class="profile-dialog-modern"
      :show-close="false"
      :append-to-body="true"
      :close-on-click-modal="false"
      @close="close"
    >
      <div slot="title" class="profile-dialog-header">
        <div class="profile-dialog-header-title">
          <i class="el-icon-user-solid" aria-hidden="true" />
          <span>{{ $t('common.myProfile') }}</span>
        </div>
        <button type="button" class="profile-dialog-close" :aria-label="$t('parentOrTeacher.Cancel')" @click="close">
          <i class="el-icon-close" />
        </button>
      </div>

      <div class="profile-dialog-body">
        <div class="profile-avatar-block">
          <el-upload
            ref="avatarUpload"
            class="avatar-uploader-modern"
            accept="image/*"
            :action="upload.uploadUrl"
            :headers="upload.headers"
            :show-file-list="false"
            :on-success="handleAvatarSuccess"
            :before-upload="beforeAvatarUpload"
          >
            <div class="avatar-square">
              <img
                v-if="upload.imageUrl"
                :src="upload.imageUrl"
                alt=""
              />
              <img v-else :src="require('@/assets/image/common/user.png')" alt="" />
              <span class="avatar-cam" aria-hidden="true">
                <i class="el-icon-camera-solid" />
              </span>
            </div>
            <span class="change-avatar-link">{{ $t('common.changeAvatar') }}</span>
          </el-upload>
        </div>

        <el-form
          :model="formData"
          :rules="rules"
          ref="profileForm"
          label-position="top"
          class="profile-form"
        >
          <el-form-item prop="userAccount">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileUsername') }}</span>
            </template>
            <div class="profile-input-wrap profile-control profile-control--readonly">
              <el-input
                v-model="formData.userAccount"
                disabled
                prefix-icon="el-icon-user"
                class="profile-input profile-input--readonly"
              />
            </div>
          </el-form-item>

          <el-form-item prop="username">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profilePreferredName') }}</span>
            </template>
            <el-input
              v-model="formData.username"
              prefix-icon="el-icon-s-custom"
              class="profile-input profile-input--editable"
            />
          </el-form-item>

          <el-form-item prop="email">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileEmail') }}</span>
            </template>
            <el-input
              v-model="formData.email"
              disabled
              prefix-icon="el-icon-postcard"
              class="profile-input profile-input--readonly"
            />
          </el-form-item>

          <el-form-item prop="sex">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileGender') }}</span>
            </template>
            <div class="profile-control profile-control--readonly">
              <i class="profile-control__icon el-icon-male" aria-hidden="true" />
              <el-select
                v-model="formData.sex"
                class="profile-select profile-select--full"
                placeholder=" "
                disabled
              >
                <el-option label="Male" value="1" />
                <el-option label="Female" value="2" />
              </el-select>
            </div>
          </el-form-item>

          <el-form-item v-if="userInfo.role !== 'P'" prop="age">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileAge') }}</span>
            </template>
            <div class="profile-control profile-control--editable">
              <i class="profile-control__icon el-icon-time" aria-hidden="true" />
              <el-input-number
                v-model="formData.age"
                :min="1"
                :max="100"
                controls-position="right"
                class="profile-input-number profile-input-number--full"
              />
            </div>
          </el-form-item>

          <el-form-item prop="country">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileNationality') }}</span>
            </template>
            <el-input
              v-model="formData.country"
              disabled
              prefix-icon="el-icon-location-outline"
              class="profile-input profile-input--readonly"
            />
          </el-form-item>

          <el-form-item v-if="userInfo.role !== 'P'" prop="grade">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileGrade') }}</span>
            </template>
            <div class="profile-control profile-control--readonly">
              <i class="profile-control__icon el-icon-reading" aria-hidden="true" />
              <el-select
                v-model="formData.grade"
                class="profile-select profile-select--full"
                :placeholder="$t('register.gradeEight')"
                disabled
              >
                <el-option :label="$t('register.gradeEight')" value="8" />
                <el-option :label="$t('register.gradeNine')" value="9" />
                <el-option :label="$t('register.gradeTen')" value="10" />
                <el-option :label="$t('register.gradeEleven')" value="11" />
                <el-option :label="$t('register.gradeTwelve')" value="12" />
              </el-select>
            </div>
          </el-form-item>

          <el-form-item prop="tel">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profilePhone') }}</span>
            </template>
            <el-input
              v-model="formData.tel"
              disabled
              prefix-icon="el-icon-phone-outline"
              class="profile-input profile-input--readonly"
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="profile-dialog-footer">
        <el-button class="profile-btn-cancel" @click="close">{{ $t('parentOrTeacher.Cancel') }}</el-button>
        <el-button type="primary" class="profile-btn-save" @click="confirm">
          <i class="el-icon-document-checked" />
          {{ $t('common.saveChanges') }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="credentialsVisible"
      width="480px"
      custom-class="profile-dialog-modern"
      :show-close="false"
      :append-to-body="true"
      :close-on-click-modal="false"
      @close="closeCredentials"
    >
      <div slot="title" class="profile-dialog-header">
        <div class="profile-dialog-header-title">
          <i class="el-icon-lock" aria-hidden="true" />
          <span>{{ $t('common.changeAccountPassword') }}</span>
        </div>
        <button type="button" class="profile-dialog-close" :aria-label="$t('parentOrTeacher.Cancel')" @click="closeCredentials">
          <i class="el-icon-close" />
        </button>
      </div>

      <div class="profile-dialog-body">
        <el-form
          :model="credentialsForm"
          :rules="credentialsRules"
          ref="credentialsForm"
          label-position="top"
          class="profile-form"
        >
          <el-form-item prop="userAccount">
            <template slot="label">
              <span class="profile-label">{{ $t('common.profileUsername') }}</span>
            </template>
            <el-input
              v-model="credentialsForm.userAccount"
              prefix-icon="el-icon-user"
              class="profile-input profile-input--editable"
            />
          </el-form-item>

          <el-form-item prop="password">
            <template slot="label">
              <span class="profile-label">{{ $t('register.password') }}</span>
            </template>
            <el-input
              v-model="credentialsForm.password"
              type="password"
              show-password
              prefix-icon="el-icon-lock"
              class="profile-input profile-input--editable"
            />
          </el-form-item>

          <el-form-item prop="rePassword">
            <template slot="label">
              <span class="profile-label">{{ $t('register.confirmPassword') }}</span>
            </template>
            <el-input
              v-model="credentialsForm.rePassword"
              type="password"
              show-password
              prefix-icon="el-icon-lock"
              class="profile-input profile-input--editable"
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="profile-dialog-footer">
        <el-button class="profile-btn-cancel" @click="closeCredentials">{{ $t('parentOrTeacher.Cancel') }}</el-button>
        <el-button type="primary" class="profile-btn-save" :loading="credentialsSaving" @click="confirmCredentials">
          <i class="el-icon-document-checked" />
          {{ $t('common.saveChanges') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { edit, findById, existAccount, changeCredentials } from '@/api/user'
import { encrypt } from '@/utils/crypto'

/**
 * @description 顶部工具栏组件
 */
export default {
  name: 'ToolBarNew',
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userInfo
    }),
    isVerifyPage () {
      return ['Verify', 'VerifyEmail', 'VerifyActivated'].includes(this.$route.name)
    },
    isStudent () {
      return this.userInfo?.role === 'S'
    },
    isLicenseExpired () {
      if (!this.licenseExpireAt) return false
      return new Date(this.licenseExpireAt).getTime() < Date.now()
    },
    licenseExpireLabel () {
      if (!this.licenseLoaded) return ''
      if (!this.licenseExpireAt) {
        return this.$t('common.noActiveLicense')
      }
      const date = this.formatExpireDate(this.licenseExpireAt)
      return this.isLicenseExpired
        ? this.$t('common.accountExpiredOn', { date })
        : this.$t('common.accountExpiresOn', { date })
    }
  },
  watch: {
    'userInfo.expireTime' (expireAt) {
      this.licenseExpireAt = expireAt || null
      this.licenseLoaded = true
    }
  },
  data () {
    const validateCredentialsAccountUnique = async (rule, value, callback) => {
      if (!value) return callback()
      if (value === this.originalUserAccount) return callback()
      try {
        const res = await existAccount({ userAccount: value })
        if (res.code === 200) {
          callback()
        } else {
          callback(new Error(this.$t('validation.accountExists')))
        }
      } catch (e) {
        callback(new Error(this.$t('validation.accountVerifyFailed')))
      }
    }
    const validateCredentialsPasswordMatch = (rule, value, callback) => {
      if (value !== this.credentialsForm.password) {
        callback(new Error(this.$t('validation.passwordMismatch')))
      } else {
        callback()
      }
    }
    return {
      licenseExpireAt: null,
      licenseLoaded: false,
      profileVisible: false,
      credentialsVisible: false,
      credentialsSaving: false,
      originalUserAccount: '',
      credentialsForm: {
        userAccount: '',
        password: '',
        rePassword: ''
      },
      credentialsRules: {
        userAccount: [
          { required: true, message: this.$t('validation.enterUserAccount'), trigger: 'blur' },
          { min: 3, max: 20, message: this.$t('validation.length3to20'), trigger: 'blur' },
          { validator: validateCredentialsAccountUnique, trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('validation.enterPassword'), trigger: 'blur' },
          { min: 6, message: this.$t('validation.passwordMin6'), trigger: 'blur' }
        ],
        rePassword: [
          { required: true, message: this.$t('validation.enterPasswordAgain'), trigger: 'blur' },
          { validator: validateCredentialsPasswordMatch, trigger: 'blur' }
        ]
      },
      formData: {
        userAccount: '',
        username: '',
        sex: '',
        password: '',
        rePassword: '',
        tel: '',
        country: '',
        email: '',
        grade: '',
        age: '',
        avatarUrl: ''
      },
      upload: {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token')
        },
        uploadUrl:
          '/api/user/avatar/upload',
        imageUrl: undefined
      },
      rules: {
        username: [
          { required: true, message: this.$t('validation.enterUsername'), trigger: 'blur' }
        ],
        sex: [
          { required: true, message: this.$t('validation.selectGender'), trigger: 'change' }
        ],
        country: [
          { required: true, message: this.$t('validation.enterNationality'), trigger: 'blur' }
        ],
        grade: [
          { required: true, message: this.$t('validation.enterGrade'), trigger: 'blur' }
        ],
        age: [
          { required: true, message: this.$t('validation.enterAge'), trigger: 'blur' },
          { type: 'number', message: this.$t('validation.ageMustBeNumber'), trigger: 'blur' }
        ]
      }
    }
  },
  mounted () {
    if (this.isStudent) {
      this.fetchStudentLicense()
    }
  },
  methods: {
    async fetchStudentLicense () {
      if (this.userInfo?.expireTime) {
        this.licenseExpireAt = this.userInfo.expireTime
        this.licenseLoaded = true
        return
      }

      if (!this.userInfo?.id) {
        this.licenseLoaded = true
        return
      }

      findById({
        id: this.userInfo.id,
        role: this.userInfo.role
      }).then((res) => {
        if (res.code !== 200) return
        const expireAt = res.data?.expireTime || null
        if (expireAt) {
          this.licenseExpireAt = expireAt
          this.syncLicenseToUserInfo(expireAt)
        }
      }).finally(() => {
        this.licenseLoaded = true
      })
    },
    syncLicenseToUserInfo (expireAt) {
      if (!expireAt || this.userInfo?.expireTime === expireAt) return
      this.$store.commit('user/SET_USER_INFO', {
        ...this.userInfo,
        expireTime: expireAt
      })
    },
    formatExpireDate (date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString(this.localeCode(), {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    },
    localeCode () {
      return this.$i18n.locale === 'fr' ? 'fr-CA' : 'en-CA'
    },
    /**
     * @description 退出登录
     */
    logout () {
      this.$store.dispatch('user/logout')
      this.$message.success(this.$t('common.logoutSuccess'))
      if (this.$route.path !== '/') {
        this.$router.push('/loginNew')
      }
    },
    /**
     * @param {string} command
     */
    handleCommand (command) {
      if (command === 'teacherManagement') {
        this.$router.push({
          path: '/teacherManagement',
          query: {}
        })
        return
      }
      if (command === 'profile') {
        const params = {
          id: this.userInfo.id,
          role: this.userInfo.role
        }
        findById(params).then((res) => {
          if (res.code !== 200) {
            return false
          }
          this.profileVisible = true
          this.formData = res.data
          this.upload.imageUrl = res.data.avatarUrl
        })
        return
      }
      if (command === 'changeCredentials') {
        this.originalUserAccount = this.userInfo.userAccount || ''
        this.credentialsForm = {
          userAccount: this.originalUserAccount,
          password: '',
          rePassword: ''
        }
        this.credentialsVisible = true
        this.$nextTick(() => {
          this.$refs.credentialsForm && this.$refs.credentialsForm.clearValidate()
        })
        return
      }
      if (command === 'logout') {
        this.logout()
        return
      }
      if (command === 'buy') {
        this.$router.push({
          path: '/creditCard',
          query: {}
        })
        return
      }
      if (command === 'billing') {
        this.$router.push({
          path: '/billing',
          query: {}
        })
      }
    },
    /**
     * @description 返回公开首页。
     */
    goHome () {
      this.$router.push('/')
    },
    close () {
      this.profileVisible = false
      this.formData = {
        userAccount: '',
        username: '',
        sex: '',
        password: '',
        rePassword: '',
        tel: '',
        country: '',
        email: '',
        grade: '',
        age: '',
        avatarUrl: ''
      }
      this.upload.imageUrl = undefined
    },
    closeCredentials () {
      this.credentialsVisible = false
      this.credentialsSaving = false
      this.originalUserAccount = ''
      this.credentialsForm = {
        userAccount: '',
        password: '',
        rePassword: ''
      }
      this.$refs.credentialsForm && this.$refs.credentialsForm.resetFields()
    },
    confirmCredentials () {
      this.$refs.credentialsForm.validate((valid) => {
        if (!valid) return
        this.credentialsSaving = true
        changeCredentials({
          id: this.userInfo.id,
          role: this.userInfo.role,
          userAccount: this.credentialsForm.userAccount,
          password: encrypt(this.credentialsForm.password)
        }).then((res) => {
          if (res.code !== 200) {
            this.$message.error(res.msg || this.$t('common.changeAccountPasswordFailed'))
            return
          }
          this.$message.success(this.$t('common.changeAccountPasswordSuccess'))
          this.$store.commit('user/SET_USER_INFO', {
            ...this.userInfo,
            userAccount: this.credentialsForm.userAccount
          })
          this.closeCredentials()
        }).catch(() => {
          this.$message.error(this.$t('common.changeAccountPasswordFailed'))
        }).finally(() => {
          this.credentialsSaving = false
        })
      })
    },
    confirm () {
      this.$refs.profileForm.validate((valid) => {
        if (valid) {
          const params = JSON.parse(JSON.stringify(this.formData))
          params.id = this.userInfo.id
          params.role = this.userInfo.role
          params.avatarUrl = this.upload.imageUrl
          edit(params).then((res) => {
            if (res.code !== 200) {
              return false
            }
            this.$message.success('Profile updated successfully')
            const updatedUserInfo = { ...this.userInfo, avatarUrl: this.upload.imageUrl }
            this.$store.commit('user/SET_USER_INFO', updatedUserInfo)
            this.close()
          })
        }
      })
    },
    handleAvatarSuccess (res) {
      if (res.code === 200) {
        this.upload.imageUrl = res.data
        this.$message.success('修改成功')
      } else {
        this.$message.error(res.msg)
      }
    },
    beforeAvatarUpload (file) {
      const isJPG = file.type.indexOf('image/') > -1
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.warning('上传头像只能是图片格式!')
      }
      if (!isLt2M) {
        this.$message.warning('上传头像图片大小不能超过 2MB!')
      }
      return isJPG && isLt2M
    }
  }
}
</script>

<style scoped>
.tagline{
  width: 100%;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #fff;
  background: linear-gradient(115deg, #7c4dff 0%, #6366f1 45%, #2563eb 100%);
  padding: 12px;
}
.landing-header {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 12px 0;
  box-shadow: none;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  height: 52px;
}
.header-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  margin-left: 200px;
}
.header-logo-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: block;
}
.header-logo-text {
  font-size: 1.4rem;
  letter-spacing: -0.01em;
}
.header-logo-bold {
  font-weight: 700;
  color: #020617;
}
.header-logo-light {
  font-weight: 700;
  color: #020617;
}
.header-brand:hover .header-logo-bold { color: #020617; }
.header-brand:hover .header-logo-light { color: #020617; }
.header-nav {
  display: flex;
  align-items: center;
  gap: 0;
}
.header-link {
  font-size: 16px;
  font-weight: 400;
  color: #4b5563;
  text-decoration: none;
  transition: color 0.2s;
  padding: 0 14px;
}
.header-link:hover {
  color: #111827;
}
.header-sep {
  display: none;
}
.header-cta {
  display: flex;
  align-items: center;
}
.btn-cta-nav {
  padding: 10px 22px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  background: linear-gradient(90deg, #f97316 0%, #ef4444 100%);
  transition: opacity 0.2s, transform 0.2s;
  position: relative;
  overflow: hidden;
  height: 48px;
}
.btn-cta-nav:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}
@media (max-width: 768px) {
  .header-nav { display: none; }
}
.tool-bar {
  position: relative;
  display: flex;
  align-items: center;
  height: 90px;
  z-index: 1;
  justify-content: space-between;
  flex-direction: column;
}

.logo {
  display: flex;
  align-items: center;
  img {
    height: 40px;
    width: 40px;
    margin: 0 16px 0 50px;
    border-radius: 50%;
  }
  div {
    font-family: Microsoft YaHei, sans-serif;
    font-weight: bold;
    font-size: 25px;
    color: #0f172a;
    height: 60px;
    display: flex;
    align-items: center;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 50px;
  flex-shrink: 0;
  position: absolute;
  right: 0;
  z-index: 201;
  top: 38px;
  height: 52px;
}

.license-expire-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #5b21b6;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.license-expire-badge i {
  font-size: 14px;
}

.license-expire-badge--expired {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #e11d48;
}

.toolbar-home-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 86px;
  height: 34px;
  padding: 0 16px;
  color: #667085;
  background: transparent;
  border: 0;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.toolbar-home-btn:hover,
.toolbar-home-btn:focus-visible {
  color: #111827;
  background: #f8fafc;
  outline: none;
}

.toolbar-home-btn i {
  font-size: 16px;
}

/** 下拉触发器：白底胶囊 + 细边框 + 紫/灰图标 */
.user-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px 8px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.user-dropdown-trigger:hover,
.user-dropdown-trigger:focus-visible {
  border-color: #d1d5db;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.07);
}

.user-trigger-icon {
  font-size: 17px;
  color: #5b21b6;
  flex-shrink: 0;
}

.user-trigger-name {
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-trigger-caret {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
}
/* —— My Profile 弹窗内容（与模板同组件，scoped 生效） —— */
.profile-dialog-body {
  padding: 0 2px 4px;
}

.profile-avatar-block {
  display: flex;
  justify-content: center;
  margin-bottom: 22px;
}

.avatar-uploader-modern {
  text-align: center;
  cursor: pointer;
}

.avatar-uploader-modern :deep(.el-upload) {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: none;
  background: transparent;
}

.avatar-square {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 18px;
  background: linear-gradient(145deg, #6d28d9 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 28px rgba(91, 33, 182, 0.28);
  overflow: visible;
}

.avatar-square img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

.avatar-cam {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #ede9fe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5b21b6;
  font-size: 14px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
}

.change-avatar-link {
  display: block;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #6d28d9;
}

.change-avatar-link:hover {
  color: #5b21b6;
  text-decoration: underline;
}

.profile-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

.profile-form :deep(.el-form-item__label) {
  padding: 0 !important;
  line-height: 1.2 !important;
}

.profile-form :deep(.el-form-item__label::before) {
  color: #7c3aed !important;
}

.profile-form :deep(.el-form-item__content) {
  margin-left: 0 !important;
}

.profile-label {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
}

/* 统一：前缀图标在控件内部左侧（与 el-input prefix 对齐） */
.profile-form :deep(.el-input .el-input__prefix) {
  left: 12px;
  color: #94a3b8;
}

.profile-form :deep(.profile-input .el-input__inner) {
  border-radius: 10px !important;
  height: 44px !important;
  font-size: 14px;
  padding-left: 40px;
}

/* 可编辑：白底，与只读区分 */
.profile-form :deep(.profile-input--editable .el-input__inner) {
  background: #fff !important;
  border: 1px solid #cbd5e1 !important;
  color: #0f172a !important;
}

.profile-form :deep(.profile-input--editable .el-input__inner:focus) {
  border-color: #a78bfa !important;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.12);
}

/* 只读 / 禁用：浅灰底 */
.profile-form :deep(.profile-input--readonly .el-input__inner),
.profile-form :deep(.profile-input--readonly.is-disabled .el-input__inner) {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  color: #64748b !important;
}

.profile-input-wrap {
  position: relative;
}

.profile-input-wrap :deep(.profile-input--readonly .el-input__inner) {
  padding-right: 96px;
}

.readonly-pill {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: #e2e8f0;
  padding: 4px 10px;
  border-radius: 8px;
  pointer-events: none;
}

/* el-select / el-input-number：外层容器 + 内部左侧图标 */
.profile-control {
  position: relative;
  width: 100%;
}

.profile-control__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  font-size: 16px;
  color: #94a3b8;
  pointer-events: none;
  line-height: 1;
}

.profile-control :deep(.el-input__inner) {
  padding-left: 40px !important;
  border-radius: 10px !important;
  height: 44px !important;
  line-height: 44px !important;
  font-size: 14px;
}

.profile-control--readonly :deep(.el-input__inner) {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  color: #64748b !important;
}

.profile-control--readonly :deep(.el-input.is-disabled .el-input__inner) {
  background: #f1f5f9 !important;
  color: #64748b !important;
}

.profile-control--editable :deep(.el-input__inner) {
  background: #fff !important;
  border: 1px solid #cbd5e1 !important;
  color: #0f172a !important;
}

.profile-control--editable :deep(.el-input__inner:focus) {
  border-color: #a78bfa !important;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.12);
}

.profile-select--full {
  width: 100%;
}

.profile-form :deep(.profile-select--full .el-input__suffix) {
  right: 8px;
}

.profile-input-number--full {
  width: 100%;
}
.profile-input-number--full :deep(.el-input-number__decrease){
    display: none;
}
.profile-input-number--full :deep(.el-input-number__increase){
    display: none;
}
.profile-form :deep(.profile-input-number .el-input__inner) {
  text-align: left;
}

.profile-dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.profile-btn-cancel {
  min-width: 108px;
  height: 40px !important;
  border-radius: 10px !important;
  font-weight: 600;
  color: #475569 !important;
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
}

.profile-btn-save.el-button--primary {
  min-width: 148px;
  height: 40px !important;
  border-radius: 10px !important;
  font-weight: 700;
  padding: 0 18px !important;
  background: linear-gradient(90deg, #6d28d9 0%, #5b21b6 100%) !important;
  border: none !important;
  box-shadow: 0 8px 20px rgba(91, 33, 182, 0.28);
}

.profile-btn-save i {
  margin-right: 6px;
  font-size: 15px;
}
</style>

<style lang="scss">
/* append-to-body 的弹窗外壳需非 scoped */
.el-dialog.profile-dialog-modern {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.14), 0 12px 28px rgba(91, 33, 182, 0.12) !important;
}

.el-dialog.profile-dialog-modern .el-dialog__header {
  padding: 0 !important;
  border-bottom: none !important;
}

.profile-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: linear-gradient(135deg, #5b21b6 0%, #6d28d9 50%, #7c3aed 100%);
  color: #fff;
}

.profile-dialog-header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.profile-dialog-header-title i {
  font-size: 20px;
  opacity: 0.95;
}

.profile-dialog-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s;
}

.profile-dialog-close:hover {
  background: rgba(255, 255, 255, 0.28);
}

.profile-dialog-close i {
  font-size: 16px;
  font-weight: 700;
}

.el-dialog.profile-dialog-modern .el-dialog__body {
  padding: 22px 24px 12px !important;
  border: none !important;
  max-height: min(62vh, 560px);
  overflow-y: auto;
  background: #fff;
}

.el-dialog.profile-dialog-modern .el-dialog__footer {
  padding: 16px 24px 22px !important;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}

/* 用户菜单下拉：菜单 ul 含 el-dropdown-menu el-popper，并 append 到 body，须用全局选择器 */
body > .el-dropdown-menu.el-popper.toolbar-user-dropdown,
.el-dropdown-menu.el-popper.toolbar-user-dropdown {
  margin-top: 8px !important;
  padding: 8px !important;
  border: 1px solid rgba(226, 232, 240, 0.95) !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06) !important;
  background: #fff !important;
  z-index: 5000 !important;
  min-width: 200px;
}

.toolbar-user-dropdown.el-dropdown-menu .el-dropdown-menu__item {
  padding: 0 !important;
  line-height: normal !important;
}

.toolbar-user-dropdown.el-dropdown-menu .el-dropdown-menu__item:not(.is-disabled):hover,
.toolbar-user-dropdown.el-dropdown-menu .el-dropdown-menu__item:focus {
  background-color: transparent !important;
}

.toolbar-dd-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 11px 14px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  box-sizing: border-box;
}

.toolbar-dd-text {
  flex: 1;
  min-width: 0;
}

.toolbar-dd-item--teacher .toolbar-dd-inner i {
  color: #6366f1;
  font-size: 16px;
}

.toolbar-dd-item--teacher .toolbar-dd-text {
  color: #334155;
}

.toolbar-dd-item--teacher:not(.is-disabled):hover .toolbar-dd-inner {
  background: #eef2ff;
}

.toolbar-dd-item--profile .toolbar-dd-inner i {
  color: #5b21b6;
  font-size: 16px;
}

.toolbar-dd-item--profile .toolbar-dd-text {
  color: #5b21b6;
}

.toolbar-dd-item--profile:not(.is-disabled):hover .toolbar-dd-inner {
  background: #ddd6fe;
}

.toolbar-dd-item--logout .toolbar-dd-inner i {
  color: #e11d48;
  font-size: 16px;
}

.toolbar-dd-item--logout .toolbar-dd-text {
  color: #e11d48;
}

.toolbar-dd-item--logout:not(.is-disabled):hover .toolbar-dd-inner {
  background: #fff1f2;
}
</style>
